import moment from 'moment';

import { measureCoverQuoteEm } from './coverQuoteMetrics';

const SYSTEM_SENDER = '___SYSTEM___';
const COVER_PLACEHOLDER_TEXT_RE = /end-to-end encrypted|end to end encrypted|end-to-end encryption|chiffrés? de bout en bout|chiffrement de bout en bout|cifrados? de extremo a extremo|crittografat|ende-zu-ende-verschlüsselt|end-to-end versleuteld|criptografia de ponta a ponta|エンドツーエンド|this message was deleted|ce message a été supprimé|waiting for this message|en attente de ce message/;

export const COVER_QUOTE_WORD_LIMIT = 7;
export const COVER_QUOTE_CUSTOM_WORD_LIMIT = 7;
export const COVER_QUOTE_MAX_LENGTH = 120;
export const COVER_BYLINE_MAX_LENGTH = 24;
export const COVER_GROUP_TITLE_MAX_LENGTH = 40;
export const COVER_CHORUS_MAX_LENGTH = 48;
export const COVER_HOUR_MAX_LENGTH = 10;
export const COVER_DATE_MAX_LENGTH = 36;
export const COVER_SPAN_MAX_LENGTH = 42;
export const COVER_INCIPIT_VERSION = 3;
export const COVER_DUST_HOUR_LIMIT = 8;
export const COVER_DUST_OPACITY = 0.16;
// Repli des toiles claires hors nuancier. Les six toiles ont chacune leur encre.
export const COVER_INCIPIT_TITLE_INK = '#004D40';
// Même poids que le vert de la sauge : une encre foncée, dans la couleur de la toile.
const COVER_TONE_INKS = {
	'#B0D8C8': '#004D40',
	'#B0D0E8': '#0F4468',
	'#F0C8B0': '#9A4010',
	'#F5F0E6': '#6B4F30',
	'#C8BCE0': '#3F2A6E',
	'#EBC4C4': '#7A2838',
};
const COVER_LIGHT_INK_LUMA = 0.55;
const COVER_DARK_CLOTH_LUMA = 0.45;
export const COVER_STYLE_PHOTO = 'photo';
export const COVER_STYLE_INCIPIT = 'incipit';
export const DEFAULT_COVER_STYLE = COVER_STYLE_INCIPIT;

// Choix explicite d'abord. Une couverture déjà versionnée (1 ou 2) sans choix
// reste photo : c'était le seul modèle. Une couverture neuve, sans version, s'ouvre sans photo.
export function getCoverStyle(cover = {}) {
	if(cover.style === COVER_STYLE_INCIPIT || cover.style === COVER_STYLE_PHOTO) {
		return cover.style;
	}
	if(cover.version === COVER_INCIPIT_VERSION) {
		return COVER_STYLE_INCIPIT;
	}
	if(cover.version) {
		return COVER_STYLE_PHOTO;
	}
	return DEFAULT_COVER_STYLE;
}

export function isIncipitCoverStyle(cover = {}) {
	return getCoverStyle(cover) === COVER_STYLE_INCIPIT;
}

function coverHexLuminance(hex = '') {
	const raw = `${hex || ''}`.replace('#', '');
	if(raw.length < 6) {
		return 0;
	}
	const channel = index => parseInt(raw.slice(index, index + 2), 16) / 255;
	return (0.2126 * channel(0)) + (0.7152 * channel(2)) + (0.0722 * channel(4));
}

// Toile du nuancier : encre ton sur ton. Toile foncée : on garde l’encre claire.
export function getCoverIncipitQuoteColor(coverData = {}) {
	const text = coverData.text || '';
	if(text && coverHexLuminance(text) > COVER_LIGHT_INK_LUMA) {
		return text;
	}
	const cloth = `${coverData.color || ''}`.trim().toUpperCase();
	if(COVER_TONE_INKS[cloth]) {
		return COVER_TONE_INKS[cloth];
	}
	if(cloth && coverHexLuminance(cloth) < COVER_DARK_CLOTH_LUMA) {
		return '#FBF8F2';
	}
	return COVER_INCIPIT_TITLE_INK;
}

const FLYLEAF_PAPER = '#FFFFFF';
// Au-dessus, la toile se confond avec le papier. On reprend alors l'encre du titre.
const FLYLEAF_CLOTH_ON_PAPER_MAX_LUMA = 0.88;

// Page de garde : même dessin que la couverture, papier blanc.
// L'encre est la toile (pêche si la couverture est pêche). L'ivoire, trop proche
// du blanc, reprend le brun du titre, pas le vert de la sauge.
export function getCoverFlyleafColors(coverData = {}) {
	const cloth = coverData.color || '#EBC4C4';
	const clothTooCloseToPaper = coverHexLuminance(cloth) > FLYLEAF_CLOTH_ON_PAPER_MAX_LUMA;
	return {
		background: FLYLEAF_PAPER,
		text: clothTooCloseToPaper ? getCoverIncipitQuoteColor(coverData) : cloth,
	};
}

export const COVER_FIELD_MAX_LENGTH = {
	quote: COVER_QUOTE_MAX_LENGTH,
	hour: COVER_HOUR_MAX_LENGTH,
	dateLabel: COVER_DATE_MAX_LENGTH,
	span: COVER_SPAN_MAX_LENGTH,
	bylineLeft: COVER_GROUP_TITLE_MAX_LENGTH,
	bylineRight: COVER_BYLINE_MAX_LENGTH,
};

// Corps du plat /preview. 1cqi = 1 % de la boîte de contenu de .cover-incipit
// (le padding latéral est déjà retiré). PDF et page de garde appliquent les ratios
// à cette même largeur intérieure : PhantomJS ne comprend pas cqi, PdfKit non plus.
// Méta : une seule voix Regular. L’heure est le moment, un peu plus lisible.
// Date, noms et période partagent le même corps, comme le colophon Vahinn.
export const COVER_TYPE_RATIO = {
	// 14 mm sur le plat de 135 mm. L'aperçu reprend le même pourcentage.
	padX: 14 / 135,
	padTop: 0.11,
	padBottom: 0.12,
	body: 1,
	quote: 0.26,
	hour: 0.037,
	date: 0.037,
	name: 0.037,
	span: 0.037,
	dust: 0.044,
	groupPadTop: 0.28,
	groupName: 0.085,
	groupSpan: 0.045,
	groupQuote: 0.055,
	groupHour: 0.055,
	groupDate: 0.036,
	chorus: 0.038,
	footerBottom: 0.08,
	trackingDate: 0.08,
	trackingDust: 0.08,
	trackingChorus: 0.08,
	// Même approche que le CSS du preview (letter-spacing: -0.04em).
	trackingQuote: -0.04,
	trackingNameGroup: 0.03,
	gapQuoteHour: 1.8,
	gapHourDate: 0,
	gapBylineSpan: 0.45,
	trackingWhen: 0.04,
};

// Corps du titre (premier SMS), en part de la largeur intérieure (cqi).
// Court : un mot par ligne. Plus long : une phrase entière, arrêtée sur un mot.
// Si la phrase ne tient pas, elle finit par « … » pour montrer la suite.
export const COVER_QUOTE_SIZE = {
	oneLineMin: 0.14,
	oneLineMax: 0.38,
	twoLines: 0.16,
	twoLinesMin: 0.12,
};

export const COVER_GROUP_QUOTE_SIZE = {
	oneLineMin: COVER_TYPE_RATIO.groupQuote,
	oneLineMax: COVER_TYPE_RATIO.groupQuote,
	twoLines: COVER_TYPE_RATIO.groupQuote * 0.75,
	twoLinesMin: COVER_TYPE_RATIO.groupQuote * 0.65,
	body: 1,
};

// Marge sur la mesure : l’approche et l’arrondi des navigateurs varient un peu.
const COVER_QUOTE_FIT = 0.99;
const COVER_QUOTE_STEP = 0.0025;

export function getCoverContentWidth(width) {
	return width * (1 - (COVER_TYPE_RATIO.padX * 2));
}

export function getCoverTypeMetrics(width) {
	const inner = getCoverContentWidth(width);
	const px = ratio => Math.round(inner * ratio * 10) / 10;
	return {
		padX: px(COVER_TYPE_RATIO.padX),
		quote: px(COVER_TYPE_RATIO.quote),
		hour: px(COVER_TYPE_RATIO.hour),
		date: px(COVER_TYPE_RATIO.date),
		name: px(COVER_TYPE_RATIO.name),
		span: px(COVER_TYPE_RATIO.span),
		dust: px(COVER_TYPE_RATIO.dust),
		groupName: px(COVER_TYPE_RATIO.groupName),
		groupSpan: px(COVER_TYPE_RATIO.groupSpan),
		groupQuote: px(COVER_TYPE_RATIO.groupQuote),
		groupHour: px(COVER_TYPE_RATIO.groupHour),
		groupDate: px(COVER_TYPE_RATIO.groupDate),
		chorus: px(COVER_TYPE_RATIO.chorus),
	};
}

export function getCoverQuotePx(width, ratio) {
	return Math.round(getCoverContentWidth(width) * ratio * 10) / 10;
}

// fontBase : dossier des polices en URL absolue. Le HTML du livre a une <base>
// relative, les url('../../fonts/…') de book.css n’y trouvent rien.
export function buildCoverTypeCss(width, { fontBase = '' } = {}) {
	const m = getCoverTypeMetrics(width);
	const fontFaces = fontBase ? `
@font-face { font-family: 'DM Sans'; src: url('${fontBase}/DMSans-Regular.ttf') format('truetype'); font-weight: 400; font-style: normal; }
@font-face { font-family: 'DM Sans'; src: url('${fontBase}/DMSans-ExtraBold.ttf') format('truetype'); font-weight: 800; font-style: normal; }
` : '';
	return `${fontFaces}
.cover-incipit-page,
.page.cover.cover-incipit-page {
	padding: 0 !important;
	font-family: "DM Sans", Helvetica, Arial, sans-serif;
}
.cover-incipit {
	font-family: "DM Sans", Helvetica, Arial, sans-serif;
}
.cover-incipit__hour-mark { font-size: ${m.dust}px; opacity: ${COVER_DUST_OPACITY}; }
.cover-incipit__quote { font-size: ${m.quote}px; line-height: 0.92 !important; font-family: "DM Sans", Helvetica, Arial, sans-serif; font-weight: 800; letter-spacing: ${COVER_TYPE_RATIO.trackingQuote}em; }
.cover-incipit__time { line-height: 1 !important; }
.cover-incipit__name { line-height: 1.4 !important; padding: 0.15em 0 0.2em; }
.cover-incipit__time { font-size: ${m.hour}px; }
.cover-incipit__when { font-size: ${m.date}px; }
.cover-incipit__name { font-size: ${m.name}px; }
.cover-incipit__span { font-size: ${m.span}px; }
.cover-incipit--group .cover-incipit__name { font-size: ${m.groupName}px; }
.cover-incipit--group .cover-incipit__span { font-size: ${m.groupSpan}px; }
.cover-incipit--group .cover-incipit__quote { font-size: ${m.groupQuote}px; }
.cover-incipit--group .cover-incipit__time { font-size: ${m.groupHour}px; }
.cover-incipit--group .cover-incipit__when { font-size: ${m.groupDate}px; }
.cover-incipit__chorus { font-size: ${m.chorus}px; }
`.trim();
}

// Heures en cadre, autour de la phrase (bande centrale libre) et hors du crédit
// en bas à gauche. x ≤ 84 pour qu’un « 00:00 » ne sorte pas du bord.
const COVER_DUST_SLOTS = [
	{ x: 6, y: 6 },
	{ x: 46, y: 5 },
	{ x: 78, y: 6 },
	{ x: 84, y: 18 },
	{ x: 6, y: 20 },
	{ x: 5, y: 66 },
	{ x: 84, y: 68 },
	{ x: 48, y: 72 },
];

const GROUP_NAME_PATTERNS = [
	/(?:a créé (?:le |ce |du )?groupe|avez créé le groupe|created (?:the |this )?group)\s+[«""']?\s*([^»""'\n]+)/i,
	/(?:a changé le sujet(?: du groupe)? (?:en|pour)|changed the subject to)\s+[«""']?\s*([^»""'\n]+)/i,
	/(?:vous avez nommé le groupe|named the group)\s+[«""']?\s*([^»""'\n]+)/i,
];

export function decodeCoverText(text = '') {
	if(!text) {
		return '';
	}
	try {
		return decodeURIComponent(text);
	} catch (err) {
		return text;
	}
}

function isCoverPlaceholderText(text = '') {
	const normalized = String(text)
		.replace(/[\u2018\u2019\u02BC]/g, "'")
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
	if(!normalized) {
		return true;
	}
	return COVER_PLACEHOLDER_TEXT_RE.test(normalized);
}

function splitCoverWords(text = '') {
	return decodeCoverText(text).replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
}

export function truncateCoverQuote(text, { words = COVER_QUOTE_WORD_LIMIT, ellipsis = true } = {}) {
	const parts = splitCoverWords(text);
	if(!parts.length) {
		return '';
	}
	if(parts.length <= words) {
		return parts.join(' ');
	}
	const sliced = parts.slice(0, words).join(' ');
	return ellipsis ? `${sliced}…` : sliced;
}

export function sanitizeCoverQuoteInput(text, { words = COVER_QUOTE_CUSTOM_WORD_LIMIT } = {}) {
	const raw = decodeCoverText(text);
	if(!raw) {
		return '';
	}
	const hasTrailingSpace = /\s$/.test(raw);
	const parts = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
	if(!parts.length) {
		return hasTrailingSpace ? ' ' : '';
	}
	if(parts.length > words) {
		return parts.slice(0, words).join(' ');
	}
	const next = hasTrailingSpace ? `${parts.join(' ')} ` : parts.join(' ');
	return clampCoverInput(next, COVER_QUOTE_MAX_LENGTH);
}

export function clampCoverInput(text = '', maxLength = 0) {
	const raw = decodeCoverText(`${text || ''}`);
	if(!maxLength || raw.length <= maxLength) {
		return raw;
	}
	return raw.slice(0, maxLength);
}

export function coverQuoteSize(quote = '') {
	return `q${layoutCoverQuote(quote).lines}`;
}

// Plus long début de `text` (caractère par caractère) qui satisfait `fits`.
function longestFittingPrefix(text, fits) {
	const chars = Array.from(text);
	const size = chars.findIndex((char, index) => !fits(chars.slice(0, index + 1).join('')));
	return size === -1 ? text : chars.slice(0, size).join('');
}

function cutCoverQuoteWord(word, roomEm) {
	return longestFittingPrefix(word, piece => measureCoverQuoteEm(piece) <= roomEm);
}

function wrapCoverQuoteRows(words, roomEm) {
	const rows = [];
	let current = '';
	words.forEach(word => {
		const piece = measureCoverQuoteEm(word) > roomEm ? cutCoverQuoteWord(word, roomEm) : word;
		const next = current ? `${current} ${piece}` : piece;
		if(measureCoverQuoteEm(next) <= roomEm) {
			current = next;
			return;
		}
		if(current) {
			rows.push(current);
		}
		current = piece;
	});
	if(current) {
		rows.push(current);
	}
	return rows;
}

// Le trop-plein se termine par « … » après un mot entier. On ne coupe un mot
// que s’il est seul et plus large que la ligne.
function endCoverQuoteRow(row, roomEm) {
	const words = `${row || ''}`.replace(/…+$/g, '').trim().split(/\s+/).filter(Boolean);
	while(words.length > 1) {
		const candidate = `${words.join(' ')}…`;
		if(measureCoverQuoteEm(candidate) <= roomEm) {
			return candidate;
		}
		words.pop();
	}
	const only = (words[0] || '').replace(/[\s,;:.…]+$/g, '');
	if(only && measureCoverQuoteEm(`${only}…`) <= roomEm) {
		return `${only}…`;
	}
	let next = only;
	while(next && measureCoverQuoteEm(`${next}…`) > roomEm) {
		next = next.slice(0, -1).replace(/[\s,;:.]+$/g, '');
	}
	return next ? `${next}…` : '…';
}

function applyTruncationEllipsis(rows, roomEm) {
	const next = rows.slice();
	while(next.length) {
		const marked = endCoverQuoteRow(next[next.length - 1], roomEm);
		if(marked.replace(/…/g, '').trim()) {
			next[next.length - 1] = marked;
			return next;
		}
		next.pop();
	}
	return ['…'];
}

// Affiche : 2 ou 3 mots, un par ligne, même corps (le plus long mot décide).
// Si regrouper deux mots courts ne réduit pas ce corps, on garde deux lignes.
const COVER_POSTER_WORDS = 3;
const COVER_POSTER_MIN = 0.18;
const COVER_POSTER_MAX = 0.32;
// Phrase plus longue : 2 ou 3 lignes, encore grande, coupée sur une virgule
// ou sur l’équilibre des lignes. On s’arrête sur un mot entier.
const COVER_PHRASE_MIN = 0.11;
const COVER_PHRASE_MAX = 0.24;
const COVER_PHRASE_LINES = 3;
const COVER_DANGLING_WORDS = new Set([
	'a', 'à', 'au', 'aux', 'de', 'du', 'des', 'le', 'la', 'les', 'un', 'une',
	'et', 'ou', 'que', 'qui', 'je', 'tu', 'on', 'me', 'te', 'se', 'en', 'y',
	'pour', 'dans', 'sur', 'avec', 'sans', 'par', 'vers', 'chez',
	'the', 'to', 'of', 'and', 'or', 'in', 'at', 'for', 'my', 'your',
	'el', 'los', 'las', 'una', 'del', 'al',
	'il', 'lo', 'gli', 'di', 'da', 'su', 'per',
	'der', 'die', 'das', 'und', 'zu', 'von', 'im', 'am',
	'het', 'een', 'van', 'op',
	'os', 'as', 'um', 'uma', 'do', 'da', 'em', 'no', 'na',
]);

function posterCoverRows(parts) {
	const rows = [];
	parts.forEach(part => {
		if(/^[?!.…,;:]+$/.test(part) && rows.length) {
			rows[rows.length - 1] = `${rows[rows.length - 1]} ${part}`;
			return;
		}
		rows.push(part);
	});
	if(rows.length < 3) {
		return rows;
	}
	// « Héberge / le / back » a le même corps que « Héberge / le back » :
	// la ligne en plus isole un mot court sans agrandir le titre.
	const stackedWide = widestCoverRow(rows);
	const balanced = splitBalancedRows(rows);
	if(widestCoverRow(balanced) <= stackedWide + 0.001) {
		return balanced;
	}
	return rows;
}

function isDanglingCoverWord(word = '') {
	const raw = `${word}`
		.replace(/[\u2018\u2019\u02BC]/g, "'")
		.replace(/[.,;:!?…]+$/g, '')
		.toLowerCase();
	if(!raw) {
		return true;
	}
	if(COVER_DANGLING_WORDS.has(raw)) {
		return true;
	}
	return /^[djlmnstc]'/i.test(raw);
}

function coverPhraseParts(parts, words) {
	let kept = parts.slice(0, words);
	if(parts.length <= words) {
		return kept;
	}
	while(kept.length > 2 && isDanglingCoverWord(kept[kept.length - 1])) {
		kept = kept.slice(0, -1);
	}
	return kept;
}

function splitBalancedRows(parts) {
	if(parts.length <= 1) {
		return [parts.join(' ')];
	}
	let best = null;
	for(let index = 1; index < parts.length; index += 1) {
		const rows = [parts.slice(0, index).join(' '), parts.slice(index).join(' ')];
		const widest = Math.max(...rows.map(row => measureCoverQuoteEm(row)));
		if(!best || widest < best.widest) {
			best = { rows, widest };
		}
	}
	return best.rows;
}

function widestCoverRow(rows) {
	return Math.max(...rows.map(row => measureCoverQuoteEm(row)), 0);
}

function phraseScore(rows) {
	return Math.max(...rows.map(row => {
		const breaksBreath = /[,:;?!…]\s+\S/.test(row) ? 1.35 : 1;
		return measureCoverQuoteEm(row) * breaksBreath;
	}), 0);
}

function bestPhraseRows(parts) {
	if(parts.length < 3) {
		return splitBalancedRows(parts);
	}
	let best = null;
	for(let start = 1; start < parts.length - 1; start += 1) {
		for(let end = start + 1; end < parts.length; end += 1) {
			const rows = [
				parts.slice(0, start).join(' '),
				parts.slice(start, end).join(' '),
				parts.slice(end).join(' '),
			];
			const score = phraseScore(rows);
			if(!best || score < best.score) {
				best = { rows, score };
			}
		}
	}
	return best.rows;
}

function phraseCoverRows(parts) {
	const candidates = [splitBalancedRows(parts)];
	if(parts.length >= 3) {
		candidates.push(bestPhraseRows(parts));
	}
	const stop = parts.findIndex((part, index) => (
		index > 0 && index < parts.length - 1 && /[,:;?!…]$/.test(part)
	));
	if(stop >= 1) {
		const left = parts.slice(0, stop + 1).join(' ');
		const right = parts.slice(stop + 1);
		candidates.push([left, right.join(' ')]);
		if(right.length >= 2) {
			candidates.push([left, ...splitBalancedRows(right)].slice(0, COVER_PHRASE_LINES));
		}
	}
	return candidates.reduce((best, rows) => (
		!best || phraseScore(rows) < phraseScore(best) ? rows : best
	)).filter(Boolean);
}

// Coupe la citation en affiche (2-3 mots), en phrase (2-3 lignes) ou en repli « … ».
// Mesure DM Sans ExtraBold. rows est dessiné tel quel par le preview, la page de garde et PdfKit.
export function layoutCoverQuote(quote, { size = COVER_QUOTE_SIZE, words = COVER_QUOTE_WORD_LIMIT } = {}) {
	const body = (size.body || COVER_TYPE_RATIO.body) * COVER_QUOTE_FIT;
	const cleaned = decodeCoverText(quote).replace(/\s+/g, ' ').trim();
	const parts = cleaned.split(' ').filter(Boolean);
	if(!parts.length) {
		return { lines: 1, rows: [], ratio: size.oneLineMin, text: '', truncated: false };
	}
	const wordCapped = parts.length > words;
	const kept = wordCapped ? parts.slice(0, words) : parts;
	const text = wordCapped ? `${kept.join(' ')}…` : kept.join(' ');
	const posterFace = size.oneLineMax >= 0.2;

	const posterRows = posterCoverRows(kept);
	if(posterFace && !wordCapped && posterRows.length >= 2 && posterRows.length <= COVER_POSTER_WORDS) {
		const widest = Math.max(...posterRows.map(row => measureCoverQuoteEm(row)));
		const posterRatio = Math.min(COVER_POSTER_MAX, size.oneLineMax, body / widest);
		if(posterRatio >= COVER_POSTER_MIN && widest <= body / COVER_POSTER_MIN) {
			return {
				lines: posterRows.length,
				rows: posterRows,
				ratio: Math.floor(posterRatio * 1000) / 1000,
				text,
				truncated: false,
			};
		}
	}

	if(posterFace) {
		const phraseParts = coverPhraseParts(parts, words);
		const truncated = phraseParts.length < parts.length;
		const phraseRows = truncated
			? applyTruncationEllipsis(phraseCoverRows(phraseParts), body / COVER_PHRASE_MIN)
			: phraseCoverRows(phraseParts);
		const widest = Math.max(...phraseRows.map(row => measureCoverQuoteEm(row)), 0);
		const phraseRatio = widest ? Math.min(COVER_PHRASE_MAX, body / widest) : 0;
		if(phraseRows.length >= 2 && phraseRatio >= COVER_PHRASE_MIN) {
			return {
				lines: phraseRows.length,
				rows: phraseRows,
				ratio: Math.floor(phraseRatio * 1000) / 1000,
				text: phraseRows.join(' '),
				truncated,
			};
		}
	}

	const oneLineRatio = Math.min(size.oneLineMax, body / measureCoverQuoteEm(text));
	if(oneLineRatio >= size.oneLineMin) {
		return {
			lines: 1,
			rows: [text],
			ratio: Math.floor(oneLineRatio * 1000) / 1000,
			text,
			truncated: wordCapped,
		};
	}

	const minRatio = size.twoLinesMin || size.twoLines;
	const steps = Math.round((size.twoLines - minRatio) / COVER_QUOTE_STEP);
	const ratios = Array.from({ length: steps + 1 }, (_, index) => Math.round((size.twoLines - (index * COVER_QUOTE_STEP)) * 10000) / 10000);
	const fitting = ratios
		.map(ratio => ({ ratio, rows: wrapCoverQuoteRows(text.split(' '), body / ratio) }))
		.find(({ rows }) => rows.length <= 2 && rows.join(' ') === text);
	if(fitting) {
		return {
			lines: fitting.rows.length,
			rows: fitting.rows,
			ratio: fitting.ratio,
			text,
			truncated: wordCapped,
		};
	}
	const roomEm = body / minRatio;
	const rows = wrapCoverQuoteRows(text.split(' '), roomEm).slice(0, 2);
	rows[rows.length - 1] = endCoverQuoteRow(rows[rows.length - 1], roomEm);
	return {
		lines: rows.length,
		rows,
		ratio: minRatio,
		text: rows.join(' '),
		truncated: true,
	};
}

// Saisie client : on n’accepte que ce qui tient en 7 mots et deux lignes,
// sans « … ». Le surplus (frappe ou collage) est simplement refusé.
export function limitCoverQuoteInput(value = '', { size = COVER_QUOTE_SIZE, words = COVER_QUOTE_WORD_LIMIT } = {}) {
	const raw = clampCoverInput(`${value || ''}`.replace(/[\r\n]+/g, ' '), COVER_QUOTE_MAX_LENGTH);
	const fits = candidate => {
		const parts = candidate.trim().split(/\s+/).filter(Boolean);
		return parts.length <= words
			&& !layoutCoverQuote(candidate, { size, words: Infinity }).truncated;
	};
	return fits(raw) ? raw : longestFittingPrefix(raw, fits);
}

export function getCoverBylineNames(participants = []) {
	return (participants || [])
		.filter(participant => (participant.name || '') !== SYSTEM_SENDER)
		.map(participant => (participant.displayName || participant.name || '').trim())
		.filter(Boolean);
}

export function getCoverBylineLayout(names = []) {
	const count = (names || []).filter(Boolean).length;
	if(count >= 3) {
		return 'group';
	}
	if(count === 2) {
		return 'duo';
	}
	return 'solo';
}

export function formatCoverChorus(names = [], maxLength = COVER_CHORUS_MAX_LENGTH) {
	const given = (names || [])
		.map(name => `${name || ''}`.trim().split(/\s+/)[0])
		.filter(Boolean);
	if(given.length < 3) {
		return '';
	}
	const parts = [];
	given.forEach(name => {
		const trial = parts.length ? `${parts.join(' · ')} · ${name}` : name;
		if(trial.length <= maxLength) {
			parts.push(name);
		}
	});
	if(!parts.length) {
		return clampCoverInput(given[0], maxLength);
	}
	if(parts.length < given.length) {
		return clampCoverInput(`${parts.join(' · ')} · …`, maxLength);
	}
	return parts.join(' · ');
}

export function flattenCoverMessages(source) {
	if(!source) {
		return [];
	}
	if(Array.isArray(source)) {
		if(source[0] && Array.isArray(source[0].messages)) {
			return source.flatMap(page => page.messages || []);
		}
		return source;
	}
	if(Array.isArray(source.pages)) {
		return source.pages.flatMap(page => page.messages || []);
	}
	if(Array.isArray(source.messages)) {
		return source.messages;
	}
	const chunks = Object.values(source);
	if(chunks.length && chunks.every(chunk => Array.isArray(chunk))) {
		return chunks.flat();
	}
	return [];
}

export function normalizeCoverMessage(message = {}) {
	const raw = message;
	return {
		date: raw.date,
		sender: raw.sender,
		text: decodeCoverText(raw.text || '').replace(/\s+/g, ' ').trim(),
		isSystem: !!(raw.isSystem || raw.sender === SYSTEM_SENDER),
		isHidden: !!raw.isHidden,
	};
}

function isCoverMessageInRange(message, start, end) {
	if(!message?.date) {
		return false;
	}
	if(!start && !end) {
		return true;
	}
	const time = moment.utc(message.date);
	if(!time.isValid()) {
		return false;
	}
	if(start && time.isBefore(moment.utc(start))) {
		return false;
	}
	if(end && time.isAfter(moment.utc(end))) {
		return false;
	}
	return true;
}

export function isCoverableMessage(message = {}) {
	const normalized = normalizeCoverMessage(message);
	return !normalized.isSystem
		&& !normalized.isHidden
		&& !!normalized.text
		&& !isCoverPlaceholderText(normalized.text);
}

export function listCoverableMessages(source, dateRange = {}) {
	return flattenCoverMessages(source)
		.map(normalizeCoverMessage)
		.filter(message => isCoverableMessage(message) && isCoverMessageInRange(message, dateRange.start, dateRange.end));
}

function cleanGroupName(raw = '') {
	return `${raw || ''}`
		.replace(/[«»""']/g, '')
		.replace(/\s+/g, ' ')
		.replace(/[.,;:]+$/, '')
		.trim()
		.slice(0, COVER_GROUP_TITLE_MAX_LENGTH);
}

export function findCoverGroupName(source) {
	const messages = flattenCoverMessages(source).map(normalizeCoverMessage);
	let found = '';
	messages.forEach(message => {
		if(found) {
			return;
		}
		GROUP_NAME_PATTERNS.some(pattern => {
			const match = (message.text || '').match(pattern);
			if(match?.[1]) {
				found = cleanGroupName(match[1]);
				return true;
			}
			return false;
		});
	});
	return found;
}

export function findCoverOpening(source, dateRange = {}) {
	const messages = listCoverableMessages(source, dateRange);
	const first = messages[0];
	if(!first) {
		return null;
	}
	return {
		quote: clampCoverInput(decodeCoverText(first.text).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH),
		quoteAt: first.date,
		sender: first.sender,
	};
}

export function selectCoverFromMessage(message) {
	const normalized = normalizeCoverMessage(message);
	const quote = clampCoverInput(decodeCoverText(normalized.text).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH);
	if(!quote) {
		return null;
	}
	return {
		quote,
		quoteAt: normalized.date,
		sender: normalized.sender,
	};
}

export function pickCoverHours(source, dateRange = {}, limit = COVER_DUST_HOUR_LIMIT) {
	const hours = [];
	const messages = listCoverableMessages(source, dateRange);
	if(!messages.length) {
		return hours;
	}
	const step = Math.max(1, Math.floor(messages.length / limit));
	messages.forEach((message, index) => {
		if(hours.length >= limit) {
			return;
		}
		if(index % step !== 0 && hours.length > 0) {
			return;
		}
		const time = moment(message.date);
		if(!time.isValid()) {
			return;
		}
		hours.push(time.format('HH:mm'));
	});
	return hours;
}

export function layoutCoverHours(hours = []) {
	return (hours || []).slice(0, COVER_DUST_SLOTS.length).map((hour, index) => ({
		hour,
		x: COVER_DUST_SLOTS[index].x,
		y: COVER_DUST_SLOTS[index].y,
	}));
}

export function formatCoverHour(date) {
	const time = moment(date);
	if(!time.isValid()) {
		return '';
	}
	return time.format('HH:mm');
}

export function formatCoverDay(date, locale = 'fr') {
	const time = moment(date);
	if(!time.isValid()) {
		return '';
	}
	return time.locale(locale).format('D MMMM YYYY');
}

export function getCoverMessageSpan(source, dateRange = {}) {
	const messages = listCoverableMessages(source, dateRange)
		.filter(message => message.date)
		.sort((left, right) => new Date(left.date) - new Date(right.date));
	if(messages.length) {
		return { start: messages[0].date, end: messages[messages.length - 1].date };
	}
	return { start: dateRange.start || '', end: dateRange.end || '' };
}

export function formatCoverSpan(start, end, locale = 'fr') {
	const from = moment(start);
	const to = moment(end);
	if(!from.isValid() || !to.isValid()) {
		return '';
	}
	const a = from.locale(locale);
	const b = to.locale(locale);
	if(a.isSame(b, 'day')) {
		return a.format('D MMMM YYYY');
	}
	if(a.isSame(b, 'month')) {
		return `${a.format('D')} - ${b.format('D MMMM YYYY')}`;
	}
	if(a.isSame(b, 'year')) {
		return `${a.format('D MMM')} - ${b.format('D MMM YYYY')}`;
	}
	return `${a.format('MMM YYYY')} - ${b.format('MMM YYYY')}`;
}

export function formatCoverSpanYears(start, end) {
	const from = moment(start);
	const to = moment(end);
	if(!from.isValid() || !to.isValid()) {
		return '';
	}
	if(from.year() === to.year()) {
		return `${from.year()}`;
	}
	return `${from.year()} - ${to.year()}`;
}

function parseCoverHourInput(value, locale = 'fr') {
	const raw = `${value || ''}`.trim().replace(/[hH]/g, ':').replace(/\s+/g, ' ');
	if(!raw) {
		return null;
	}
	const formats = ['HH:mm', 'H:mm', 'HH:mm:ss', 'LT', 'h:mm A', 'h:mm a', 'hh:mm A', 'hh:mm a'];
	const local = moment(raw, formats, locale, true);
	if(local.isValid()) {
		return local;
	}
	const english = moment(raw, ['h:mm A', 'h:mm a', 'hh:mm A', 'hh:mm a', 'h:mma', 'h:mmA'], 'en', true);
	return english.isValid() ? english : null;
}

function parseCoverDayInput(value, locale = 'fr') {
	const raw = `${value || ''}`.trim();
	if(!raw) {
		return null;
	}
	const parsed = moment(raw, ['D MMMM YYYY', 'Do MMMM YYYY', 'D MMM YYYY', 'DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD'], locale, true);
	return parsed.isValid() ? parsed : null;
}

export function mergeCoverDateTime(baseDate, { hour, dateLabel, locale = 'fr' } = {}) {
	const base = moment(baseDate);
	const next = base.isValid() ? base.clone() : moment();
	if(dateLabel != null) {
		const parsed = parseCoverDayInput(dateLabel, locale);
		if(!parsed) {
			return null;
		}
		next.year(parsed.year()).month(parsed.month()).date(parsed.date());
	}
	if(hour != null) {
		const parsed = parseCoverHourInput(hour, locale);
		if(!parsed) {
			return null;
		}
		next.hour(parsed.hour()).minute(parsed.minute()).second(parsed.second() || 0);
	}
	return next.format('YYYY-MM-DD HH:mm:ss');
}

// Premier tome qui porte encore ses messages : c'est sa première phrase
// qui s'affiche sur tous les tomes, tant qu'un tome n'a pas choisi la sienne.
export function pickFirstCoverBook(books = []) {
	const list = Array.isArray(books) ? books.filter(Boolean) : [];
	const hasMessages = book => Array.isArray(book.pages) && book.pages.some(page => (page.messages || []).length);
	const volumeZero = list.find(book => Number(book.index) === 0 && hasMessages(book));
	if(volumeZero) {
		return volumeZero;
	}
	if(list[0] && hasMessages(list[0])) {
		return list[0];
	}
	return null;
}

export function coverOpeningFromBook(book, highlights = null) {
	if(book) {
		const opening = findCoverOpening(book.pages || book, {
			start: book.start,
			end: book.end,
		});
		if(opening) {
			return opening;
		}
	}
	const first = highlights?.story?.firstMessage;
	if(first?.text && !isCoverPlaceholderText(first.text)) {
		return {
			quote: clampCoverInput(decodeCoverText(first.text).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH),
			quoteAt: first.date || '',
			sender: first.sender || '',
		};
	}
	return null;
}

// Le tome 1 s'ouvre sur son premier message. Les tomes suivants reprennent
// cette même phrase, sauf si ce tome en a choisi une autre.
export function coverQuoteForVolume(mergedCover = {}, ownCover = null, bookIndex = null) {
	if(!(bookIndex > 0)) {
		return { cover: mergedCover || {}, allowOpening: true, shareFirstOpening: false };
	}
	const own = ownCover || {};
	const merged = mergedCover || {};
	if(own.quoteCustomized) {
		return {
			allowOpening: false,
			shareFirstOpening: false,
			cover: {
				...merged,
				quote: own.quote,
				quoteCustomized: true,
				quoteAt: own.quoteAt || '',
				quoteAtCustomized: !!`${own.quoteAt || ''}`.trim(),
				hour: own.hourCustomized ? own.hour : '',
				hourCustomized: !!own.hourCustomized,
				dateLabel: own.dateLabelCustomized ? own.dateLabel : '',
				dateLabelCustomized: !!own.dateLabelCustomized,
			},
		};
	}
	if(merged.quoteCustomized && `${merged.quote || ''}`.trim()) {
		return {
			allowOpening: false,
			shareFirstOpening: false,
			cover: {
				...merged,
				hour: merged.hourCustomized ? merged.hour : '',
				hourCustomized: !!merged.hourCustomized,
				dateLabel: merged.dateLabelCustomized ? merged.dateLabel : '',
				dateLabelCustomized: !!merged.dateLabelCustomized,
			},
		};
	}
	return {
		allowOpening: false,
		shareFirstOpening: true,
		cover: {
			...merged,
			quote: '',
			quoteCustomized: false,
			quoteAt: '',
			quoteAtCustomized: false,
			hour: '',
			hourCustomized: false,
			dateLabel: '',
			dateLabelCustomized: false,
		},
	};
}

export function resolveCoverIncipit({
	cover = {},
	messages = [],
	dateRange = {},
	names = [],
	locale = 'fr',
	highlights = null,
	groupFallback = '',
	groupMessages = null,
	coverTo = null,
	allowOpening = true,
	sharedOpening = null,
} = {}) {
	const opening = allowOpening ? findCoverOpening(messages, dateRange) : null;
	const highlightFirst = allowOpening ? highlights?.story?.firstMessage : null;
	const storedQuote = `${cover.quote || ''}`;
	const hasStoredQuote = !!cover.quoteCustomized;
	const hasStoredAt = !!cover.quoteAtCustomized && !!`${cover.quoteAt || ''}`.trim();

	let quote = '';
	let quoteAt = '';

	if(hasStoredQuote) {
		quote = clampCoverInput(decodeCoverText(storedQuote).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH);
		quoteAt = hasStoredAt
			? cover.quoteAt
			: (cover.quoteAt || opening?.quoteAt || highlightFirst?.date || '');
	} else if(opening) {
		quote = opening.quote;
		quoteAt = hasStoredAt ? cover.quoteAt : opening.quoteAt;
	} else if(sharedOpening?.quote) {
		quote = clampCoverInput(decodeCoverText(sharedOpening.quote).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH);
		quoteAt = hasStoredAt ? cover.quoteAt : (sharedOpening.quoteAt || '');
	} else if(highlightFirst?.text && !isCoverPlaceholderText(highlightFirst.text)) {
		quote = clampCoverInput(decodeCoverText(highlightFirst.text).replace(/\s+/g, ' ').trim(), COVER_QUOTE_MAX_LENGTH);
		quoteAt = hasStoredAt ? cover.quoteAt : (highlightFirst.date || '');
	} else if(hasStoredAt) {
		quoteAt = cover.quoteAt;
	}

	const people = (names || []).filter(Boolean);
	// À deux ou plus, le 1er message s'affiche comme un duo, même pour un groupe.
	const counted = getCoverBylineLayout(people);
	const layout = counted === 'group' ? 'duo' : counted;
	const isGroup = layout === 'group';

	if(isGroup && !hasStoredQuote) {
		quote = '';
		quoteAt = hasStoredAt ? cover.quoteAt : '';
	}

	const spanBounds = getCoverMessageSpan(messages, dateRange);
	const autoSpan = formatCoverSpan(spanBounds.start, spanBounds.end, locale);
	const spanYears = formatCoverSpanYears(spanBounds.start, spanBounds.end);
	const autoHour = formatCoverHour(quoteAt, locale);
	const autoDate = formatCoverDay(quoteAt, locale);
	const showIncipitTime = !isGroup || !!quote;
	const hour = !showIncipitTime
		? ''
		: (cover.hourCustomized
			? clampCoverInput(cover.hour != null ? cover.hour : autoHour, COVER_HOUR_MAX_LENGTH)
			: autoHour);
	const dateLabel = !showIncipitTime
		? ''
		: (cover.dateLabelCustomized
			? clampCoverInput(cover.dateLabel != null ? cover.dateLabel : autoDate, COVER_DATE_MAX_LENGTH)
			: autoDate);
	const storedSpan = cover.spanCustomized
		? clampCoverInput(cover.span != null ? cover.span : autoSpan, COVER_SPAN_MAX_LENGTH)
		: autoSpan;
	// Duo et solo : la date du plat est le sous-titre « à » de la couverture photo
	// (jour mois année, même donnée). Un groupe garde sa période propre.
	const photoDate = coverTo != null ? `${coverTo}`.trim() : '';
	const span = !isGroup && photoDate
		? clampCoverInput(photoDate, 45)
		: storedSpan;

	let nameLeft = '';
	let nameRight = '';

	if(layout === 'duo') {
		nameLeft = clampCoverInput(cover.bylineLeft || people[0] || '', COVER_BYLINE_MAX_LENGTH);
		nameRight = clampCoverInput(cover.bylineRight || people[1] || '', COVER_BYLINE_MAX_LENGTH);
	} else if(isGroup) {
		const groupName = findCoverGroupName(groupMessages || messages) || groupFallback || '';
		nameLeft = clampCoverInput(cover.bylineLeft || groupName, COVER_GROUP_TITLE_MAX_LENGTH);
	} else {
		nameLeft = clampCoverInput(cover.bylineLeft || people[0] || '', COVER_BYLINE_MAX_LENGTH);
	}

	const fitted = layoutCoverQuote(quote, isGroup ? { size: COVER_GROUP_QUOTE_SIZE } : {});

	return {
		quote: fitted.text,
		quoteAt,
		hour,
		dateLabel,
		nameLeft,
		nameRight,
		layout,
		hours: layoutCoverHours(pickCoverHours(messages, dateRange)),
		chorus: isGroup ? formatCoverChorus(people) : '',
		size: `q${fitted.lines}`,
		quoteRatio: fitted.ratio,
		quoteRows: fitted.rows,
		span,
		spanYears,
	};
}

export function mergeLiveCover(stored = {}, live = {}) {
	const next = {
		...stored,
		quote: live.quote != null ? live.quote : stored.quote,
		quoteAt: live.quoteAt != null ? live.quoteAt : stored.quoteAt,
		quoteCustomized: !!(live.quoteTouched || stored.quoteCustomized),
		quoteAtCustomized: !!(live.quoteAtTouched || stored.quoteAtCustomized),
		bylineLeft: live.bylineLeft != null ? live.bylineLeft : stored.bylineLeft,
		bylineRight: live.bylineRight != null ? live.bylineRight : stored.bylineRight,
	};

	if(live.followMessageTime) {
		next.hourCustomized = false;
		next.dateLabelCustomized = false;
	} else {
		if(live.hourTouched || stored.hourCustomized) {
			next.hour = live.hourTouched ? live.hour : stored.hour;
			next.hourCustomized = true;
		}
		if(live.dateLabelTouched || stored.dateLabelCustomized) {
			next.dateLabel = live.dateLabelTouched ? live.dateLabel : stored.dateLabel;
			next.dateLabelCustomized = true;
		}
	}

	if(live.spanTouched || stored.spanCustomized) {
		next.span = live.spanTouched ? live.span : stored.span;
		next.spanCustomized = true;
	}

	return next;
}

export function hasCoverTextEdits(state = {}) {
	return !!(
		state.quoteTouched
		|| state.quoteAtTouched
		|| state.hourTouched
		|| state.dateLabelTouched
		|| state.spanTouched
		|| state.toTouched
	);
}
